import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";

// Links a Chat conversation's call elements on one page. MUST NOT import the
// livekit store: Call Status / Participant Count / Join Call render on pages
// that never open a call, and must not pull the video SDK into their bundle.

const PRESENCE_POLL_INTERVAL_MS = 15000;
const PRESENCE_BATCH_LIMIT = 50;
const PRESENCE_URL = "/api/v1/meeting/presence";
const IDLE_PRESENCE = Object.freeze({ active: false, count: 0 });

export function conversationCallKey({ configurationId, conversationId }) {
  return `${Number(configurationId)}:${Number(conversationId)}`;
}

function isCompleteTarget(target) {
  return Number(target?.destinationId) > 0
    && Number(target?.configurationId) > 0
    && Number(target?.conversationId) > 0;
}

export const useConversationCallStore = defineStore("conversation-call-store", {
  state: () => ({
    presenceByKey: {},
    liveCountByKey: {},
    subscriptionsByKey: {},
    videoCallKeys: {},
    openCallKey: null,
    pollTimerId: null,
    isRefreshScheduled: false,
  }),
  getters: {
    presenceFor(state) {
      return target => {
        const key = conversationCallKey(target);
        const live = state.liveCountByKey[key];
        if (typeof live === "number") {
          return { active: live > 0, count: live };
        }

        return state.presenceByKey[key] || IDLE_PRESENCE;
      };
    },
    isInCall(state) {
      return target => typeof state.liveCountByKey[conversationCallKey(target)] === "number";
    },
    isOpen(state) {
      return target => state.openCallKey === conversationCallKey(target);
    },
  },
  actions: {
    // One Video Call element per conversation per page: two would each host the
    // call's audio and keyboard shortcuts.
    registerVideoCall(target) {
      const key = conversationCallKey(target);
      if (this.videoCallKeys[key]) {
        // eslint-disable-next-line no-console
        console.error(
          `A second Video Call element is bound to conversation ${target.conversationId} on this page. `
          + "Only the first one hosts the call; remove the duplicate.",
          { target },
        );

        return false;
      }
      this.videoCallKeys[key] = true;

      return true;
    },
    unregisterVideoCall(target) {
      const key = conversationCallKey(target);
      delete this.videoCallKeys[key];
      if (this.openCallKey === key) {
        this.openCallKey = null;
      }
    },
    requestJoin(target) {
      const key = conversationCallKey(target);
      if (!this.videoCallKeys[key]) {
        // eslint-disable-next-line no-console
        console.error(
          `Join Call for conversation ${target.conversationId} has no Video Call element on this page to open. `
          + "Add a Video Call element bound to the same conversation.",
          { target },
        );

        return false;
      }
      this.openCallKey = key;

      return true;
    },
    closeCall(target) {
      if (this.openCallKey === conversationCallKey(target)) {
        this.openCallKey = null;
      }
    },
    // Pushed by the Video Call element from the live roster while it is in the
    // call; null when it leaves.
    setLiveCount(target, count) {
      const key = conversationCallKey(target);
      if (typeof count === "number") {
        this.liveCountByKey[key] = count;

        return;
      }
      delete this.liveCountByKey[key];
    },
    subscribePresence(target) {
      if (!isCompleteTarget(target)) {
        return;
      }
      const key = conversationCallKey(target);
      const existing = this.subscriptionsByKey[key];
      this.subscriptionsByKey[key] = {
        target: { ...target },
        count: (existing?.count || 0) + 1,
      };
      if (!existing) {
        this.scheduleRefresh();
      }
      if (this.pollTimerId === null && typeof window !== "undefined") {
        this.pollTimerId = window.setInterval(() => this.refreshPresenceIfVisible(), PRESENCE_POLL_INTERVAL_MS);
      }
    },
    unsubscribePresence(target) {
      const key = conversationCallKey(target);
      const existing = this.subscriptionsByKey[key];
      if (!existing) {
        return;
      }
      if (existing.count > 1) {
        existing.count -= 1;

        return;
      }
      delete this.subscriptionsByKey[key];
      if (Object.keys(this.subscriptionsByKey).length === 0 && this.pollTimerId !== null) {
        window.clearInterval(this.pollTimerId);
        this.pollTimerId = null;
      }
    },
    // Every element on a page subscribes during the same render: fetch once
    // for all of them, not once per element.
    scheduleRefresh() {
      if (this.isRefreshScheduled) {
        return;
      }
      this.isRefreshScheduled = true;
      setTimeout(() => {
        this.isRefreshScheduled = false;
        this.refreshPresence();
      }, 0);
    },
    refreshPresenceIfVisible() {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      this.refreshPresence();
    },
    refreshPresence() {
      if (!useGeneralStore().isLoggedIn) {
        return;
      }
      const batches = {};
      Object.values(this.subscriptionsByKey).forEach(({ target }) => {
        const batchKey = `${target.destinationId}:${target.configurationId}`;
        batches[batchKey] = batches[batchKey] || { target, conversationIds: [] };
        batches[batchKey].conversationIds.push(Number(target.conversationId));
      });
      Object.values(batches).forEach(({ target, conversationIds }) => {
        for (let start = 0; start < conversationIds.length; start += PRESENCE_BATCH_LIMIT) {
          this.fetchPresenceBatch(target, conversationIds.slice(start, start + PRESENCE_BATCH_LIMIT));
        }
      });
    },
    fetchPresenceBatch(target, conversationIds) {
      const params = {
        destination_id: Number(target.destinationId),
        configuration_id: Number(target.configurationId),
        conversation_ids: conversationIds.join(","),
      };
      this.apiGet(PRESENCE_URL, params, data => {
        const byId = data?.conversations || {};
        conversationIds.forEach(conversationId => {
          const entry = byId[String(conversationId)] || IDLE_PRESENCE;
          this.presenceByKey[conversationCallKey({ ...target, conversationId })] = {
            active: Boolean(entry.active),
            count: Number(entry.count || 0),
          };
        });
      }, error => {
        // Keep the last known presence: a failed poll is not "nobody is in the call".
        // eslint-disable-next-line no-console
        console.error("Chat call presence request failed; keeping the last known counts", { params, error });
      });
    },
  },
});
